import { Menu, Transition } from '@headlessui/react';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from "react-router-dom";

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const MENU_WIDTH = 224; // w-56
const ITEM_HEIGHT = 36; // px-4 py-2 text-sm
const GAP = 8;

/**
 * Row action menu rendered through a portal so it is never clipped by the
 * table's `overflow-hidden` wrapper or by any scrollable ancestor.
 */
export default function RowOptionsMenu({ options, record }) {
  const navigate = useNavigate();
  const buttonRef = useRef(null);

  return (
    <Menu as="div" className="inline-block text-left">
      {({ open, close }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20">
            <span className="sr-only">Open options</span>
            <svg xmlns="http://www.w3.org/2000/svg"
              fill="none" viewBox="0 0 24 24"
              strokeWidth={1.5} stroke="currentColor"
              className="h-5 w-5">
              <path strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
            </svg>
          </Menu.Button>

          <MenuPanel open={open} buttonRef={buttonRef} itemCount={options.length}>
            {options.map((option, oi) =>
              <Menu.Item key={oi}>
                {({ active }) => (
                  <button
                    type="button"
                    className={classNames(
                      active
                        ? 'bg-gray-50 text-gray-900'
                        : 'text-gray-600',
                      'block w-full text-left px-4 py-2 text-sm transition-colors'
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      close();
                      if (option.link) {
                        navigate(option.link);
                      } else {
                        option.onClick?.(record);
                      }
                    }}
                  >
                    {option.name}
                  </button>
                )}
              </Menu.Item>
            )}
          </MenuPanel>
        </>
      )}
    </Menu>
  );
}

/**
 * Portals the menu to <body> and anchors it to the trigger with fixed
 * positioning, flipping upwards when there is not enough room below. Stays
 * mounted while closed so the leave transition can play.
 */
function MenuPanel({ open, buttonRef, itemCount, children }) {
  const [position, setPosition] = useState(null);

  const updatePosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - GAP;
    const spaceAbove = rect.top - GAP;
    const desiredHeight = itemCount * ITEM_HEIGHT + GAP;
    const openUpwards = spaceBelow < desiredHeight && spaceAbove > spaceBelow;

    setPosition({
      left: Math.min(
        Math.max(rect.right - MENU_WIDTH, GAP),
        Math.max(window.innerWidth - MENU_WIDTH - GAP, GAP)
      ),
      top: openUpwards ? undefined : rect.bottom + GAP,
      bottom: openUpwards ? window.innerHeight - rect.top + GAP : undefined,
      maxHeight: Math.max(openUpwards ? spaceAbove : spaceBelow, ITEM_HEIGHT * 2)
    });
  }, [buttonRef, itemCount]);

  // Position before paint so the menu never flashes at a stale location.
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  // Capture-phase scroll so inner scroll containers re-anchor it too.
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  if (!position) return null;

  return createPortal(
    <Transition
      as={Fragment}
      show={open}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items
        static
        style={{
          position: 'fixed',
          left: position.left,
          top: position.top,
          bottom: position.bottom,
          width: MENU_WIDTH,
          maxHeight: position.maxHeight
        }}
        className="z-30 origin-top-right overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg focus:outline-none">
        {children}
      </Menu.Items>
    </Transition>,
    document.body
  );
}
